import React from 'react'

import {
  Tabulation,
  Axis,
  Expand,
  Filter,
  Transpose,
  All,
  Header,
  Variable,
  Statistic,
} from "retabulate-react/lib/graphqlFragments"

export const D3Context = React.createContext({
  CellRenderer: () => <td />,
  HeaderRenderer: () => <span />,
})

export const DemoTable = (term, meta, options) => (
  <Tabulation
    className="demoTable"
    dataset={`${term}Csv`}
    where={[{ key: 'Q1', values: meta.choices }]}
    corner={<span>{meta.demographic}</span>}
  >
    <Axis position="left">
      <Expand
        column={meta.code}
        mapping={meta.choices.map(c => ({ label: c, values: [c] }))}
      />
      <Header
        label="Total"
        cellStyles={totalStyle}
        labelStyles={totalStyle}
      />
    </Axis>
    <Axis position="top">
      <Variable column="RecipientEmail">
        <Statistic method="n" label=" " formatter={n => (n * 1).toLocaleString()} />
        <Statistic method="pctn" over={meta.code} label=" " formatter={n => Math.round(n * 1) + '%'} />
      </Variable>
    </Axis>
  </Tabulation>
)

export const SingleChoice = (term, meta, options) => (
  <Tabulation
    dataset={`${term}Csv`}
  >
    <Axis position="left">
      <Header label={meta.prompt} />
    </Axis>
    <Axis position="top">
      <Expand
        column={meta.code}
        delimiter={meta.multi ? meta.multi : undefined}
        mapping={meta.choices.map(c => ({
          label: c,
          values: [meta.multi == ',' ? c.replace(/,/g, '/') : c]
        }))}
      >
        <Variable column="RecipientEmail">
          <Statistic method="n" />
        </Variable>
      </Expand>
    </Axis>
  </Tabulation>
)

export const Multi = (term, meta, options) => (
  <Tabulation
    dataset={`${term}Csv`}
    corner={<span>{meta.prompt}</span>}
  >
    <Axis position="left">
      <Expand
        column={meta.code}
        delimiter={meta.multi ? meta.multi : undefined}
        orderByStatistic={{ column: "RecipientEmail", method: "n", descending: true }}
        mapping={meta.choices.map(c => ({
          label: c,
          values: [meta.multi == ',' ? c.replace(/,/g, '/') : c],
        }))}
      />
    </Axis>
    <Axis position="top">
      <Variable column={meta.code}>
        <Statistic
          method="n"
          label="n"
          formatter={val => (val * 1).toLocaleString()}
        />
        <Statistic
          method="pctn"
          label="percent"
          over={meta.code}
          cellRenderer={({ cellProps, cell }) =>
            <D3Context.Consumer>
              {({ CellRenderer }) => <CellRenderer value={{ percent: cell.value / 100 }} />}
            </D3Context.Consumer>
          }
        />
      </Variable>
    </Axis>
  </Tabulation>
)

export const Single = (term, meta, options) => (
  <Tabulation
    dataset={`${term}Csv`}
  >
    <Axis position="left">
      <Header label={meta.prompt} />
    </Axis>
    <Axis position="top">
      <Variable column={meta.code}>
        <Statistic
          method="distributionRatio"
          label="dist"
          cellRenderer={({ cellProps, cell }) =>
            <D3Context.Consumer>
              {({ CellRenderer }) => <CellRenderer value={cell.value} />}
            </D3Context.Consumer>
          }
          labelRenderer={({ cellProps, cell }) =>
            <th {...cellProps}>
              <D3Context.Consumer>
                {({ HeaderRenderer }) => <HeaderRenderer />}
              </D3Context.Consumer>
            </th>
          }
        />
      </Variable>
    </Axis>
  </Tabulation>
)

export const Matrix = (term, meta, options) => (
  <Tabulation
    dataset={`${term}Csv`}
    corner={<span>{meta.prompt}</span>}
  >
    <Axis position="left">
      <Transpose
        columns={meta.items.map((_, i) => `${meta.prefix}_${i + 1}`)}
        labelRenderer={({ cellProps, data }) =>
          <th {...cellProps}>
            {meta.items[parseInt(data.label.split('_')[1]) - 1]}
          </th>
        }
        as="q"
      />
    </Axis>
    <Axis position="top">
      <Variable column="q">
        <Statistic
          method="distributionRatio"
          label=" hi "
          labelRenderer={({ cellProps, cell }) =>
            <th {...cellProps}>
              <D3Context.Consumer>
                {({ HeaderRenderer }) => <HeaderRenderer />}
              </D3Context.Consumer>
            </th>
          }
          cellRenderer={({ cellProps, cell }) =>
            <D3Context.Consumer>
              {({ CellRenderer }) => <CellRenderer value={cell.value} />}
            </D3Context.Consumer>
          }
        />
      </Variable>
    </Axis>
  </Tabulation>
)

const totalStyle = { fontWeight: 'bold', borderTop: '1px solid #999' }

export const CompareTableMatrix = (term, meta, filter) => (
  <Tabulation
    className="detailTable"
    dataset={`${term}Csv`}
    corner={<span>{meta.prompt}</span>}
    where={filter
      ? [filter]
      : undefined
    }
  >
    <Axis position="left">
      <Transpose
        columns={meta.items.map((_, i) => `${meta.prefix}_${i + 1}`)}
        labelRenderer={({ cellProps, data }) =>
          <th {...cellProps}>
            {meta.items[parseInt(data.label.split('_')[1]) - 1]}
          </th>
        }
        as="q"
      />
      <Transpose
        columns={[meta.prefix + '_1']} as="q"
        cellStyles={totalStyle}
        cellProps={{ "data-t": true }}
        labelStyles={totalStyle}
        labelRenderer={({ cellProps }) => <th {...cellProps}>Responses</th>}
      />
    </Axis>
    <Axis position="top">
      <Expand
        column={meta.groupBy}
        mapping={meta.groupByMapping}
        total="Overall"
      >
        <Variable column="q">
          <Statistic
            methods={["distributionRatio", "n"]}
            diff={{ key: meta.groupBy }}
            cellRenderer={({ cellProps, cell: { value } }) =>
              !cellProps["data-t"]
                ? <td {...cellProps}
                  style={scoreStyles(meta.choices, value)}>
                  {scoreQ(meta.choices, value.group.distributionRatio).toFixed(1)}
                </td>
                : <td {...cellProps}>
                  {(value.group.n).toLocaleString()}
                </td>
            }
          />
        </Variable>
      </Expand>
    </Axis>
  </Tabulation>
)

const scoreStyles = (items, { group, diff }) => {
  const g = scoreQ(items, group.distributionRatio)
  const d = scoreQ(items, diff.distributionRatio)
  return ({
    backgroundColor: `rgba(${(g - d) > 0
        ? `120,120,200`
        : '200,120,120'
      },${Math.pow(Math.abs(g - d), 1.5) * 2
      })`,
  })
}

const scoreQ = (items, dist) =>
  Object.keys(dist).reduce((sum, k) =>
    sum + (items.length - items.indexOf(k)) * dist[k]
    , 0)

export const CompareTableSingle = (term, meta, filter) => (
  <Tabulation
    className="detailTable"
    dataset={`${term}Csv--${meta.code}`}
    corner={<span>{meta.prompt}</span>}
    where={filter
      ? [filter]
      : undefined
    }
  >
    <Axis position="left">
      <Expand
        column={meta.code}
        delimiter={meta.multi ? meta.multi : undefined}
        orderByStatistic={{ column: "RecipientEmail", method: "n", descending: true }}
        mapping={meta.choices.map(c => ({
          label: c,
          values: [meta.multi == ',' ? c.replace(/,/g, '/') : c],
        }))}
      />
      <Header label="Responses" cellStyles={totalStyle} labelStyles={totalStyle} />
    </Axis>
    <Axis position="top">
      <Expand
        column={meta.groupBy}
        mapping={meta.groupByMapping}
        total="Overall"
      >
        <Variable column="RecipientEmail">
          <Statistic
            methods={['pctn', 'n']}
            over={meta.code}
            diff={{ key: meta.groupBy }}
            cellStyles={{ paddingRight: '1em' }}
            cellRenderer={({ cellProps, cell }) => (
              cell.queries.filter(q => q.key === meta.code).length
                ? <td
                  {...cellProps}
                  style={{ ...cellProps.style, ...getHeatStyle(normalizeDiff(cell.value)) }}
                >
                  {Math.round(cell.value.group.pctn)}
                </td>
                : <td {...cellProps}>
                  {(cell.value.group.n).toLocaleString()}
                </td>
            )}
          />
        </Variable>
      </Expand>
    </Axis>
  </Tabulation>
)

/** correction algorithm
group
  n 20
  pct 50
  (n-total) 40 (n * 100 / pct)
diff
  n 50
  pct 10
  (n-total) 500 (n * 100 / pct)
diff - group
  total-n (diff-total - group-total) 460
  n (diff-n - group-n) 30
  pct (n / total) 30/460   
*/

// remove own group from diff
const normalizeDiff = ({ diff, group }) => ({
  group,
  diff: {
    n: diff.n - group.n,
    pctn: (diff.n - group.n)
      / (diff.n * 100 / diff.pctn -
        group.n * 100 / group.pctn)
      * 100
  },
})

export const Quadrants = ({ cellProps }) => (
  <div style={{ textAlign: 'right' }}>
    <div style={{ display: 'inline-block', fontWeight: 'normal', heigth: '1em' }}>
      lower than others &nbsp;
    </div>
    {[-20, -15, -10, 0, 10, 15, 20].map(q =>
      <div
        key={q}
        style={{
          ...getHeatStyle({ group: { pctn: q }, diff: { pctn: 0 } }),
          display: 'inline-block',
          width: '2em',
          height: '1.2em',
          marginBottom: '8px'
        }}>
        &nbsp;
      </div>
    )}
    <div style={{ display: 'inline-block', fontWeight: 'normal', height: '1em' }}>
      &nbsp; above others
    </div>
  </div>
)

const getHeatStyle = ({ group, diff }) => ({
  //backgroundColor: `rgba(120,120,250,${Math.sqrt(pctn) / 10})`,
  //color: pctn > 70 ? 'white' : 'black',
  backgroundColor: `rgba(${(group.pctn - diff.pctn) > 0
      ? `120,120,200`
      : '200,120,120'
    },${Math.pow(Math.abs(group.pctn - diff.pctn), 1.25) * 0.01
    })`,
})